/**
 * COC station view-model (COC-STATION-V1, V4-P1). PURE presentation logic --
 * no fetch, no clock read (nowMs injected) -- so the E-3 gates are testable
 * at the component level: one primary action per row state, fields not
 * sentences, honest states, explicit dates.
 *
 * REUSE: SLA class titles come from the ONE leakage view vocabulary
 * (sla-leakage-view classMeta); timestamps render through the ONE IST
 * formatter (lib/datetime-ist); ages through the ONE leakage age humanizer.
 */

import type {
  CocStationCountsView,
  CocStationRowView,
  StationSegmentView,
} from '@/dashboard/api';
import {
  classMeta,
  formatLeakageAge,
  slaBreachCountsForRows,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';
import type { OpeningLineState } from '@/dashboard/ui';

/** The five known breach classes (wire strings map through safely). */
const KNOWN_CLASSES: readonly SlaBreachClass[] = [
  'unclaimed_lead_overdue',
  'qualified_no_appointment',
  'booked_not_confirmed',
  'stuck_in_stage',
  'no_activity',
];

/** Short chip title for a breach class; unknown tokens humanize honestly. */
export function slaChipTitle(breachClass: string): string {
  const known = (KNOWN_CLASSES as readonly string[]).includes(breachClass)
    ? classMeta(breachClass as SlaBreachClass)
    : null;
  if (known !== null) return known.title;
  const spaced = breachClass.replace(/_/g, ' ').trim();
  return spaced.length > 0
    ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
    : breachClass;
}

/** Chip age text via the ONE leakage humanizer (45m / 2h 15m / 3d). */
export function slaChipAge(ageMinutes: number): string {
  return formatLeakageAge(ageMinutes);
}

/**
 * SLA-ONE-DERIVATION-AND-STATION-STRIP-V1 (Refs #315): per-class breach counts
 * over EXACTLY the station rows the list currently renders (strip == list, the
 * #372 invariant -- pass the SAME filtered array the table maps). Derived from
 * the rows' classifier-verbatim slaChips through THE ONE frontend aggregation
 * (sla-leakage-view.slaBreachCountsForRows) -- never a second evaluator. A row
 * whose slaChips are ABSENT (an older/partial wire) contributes nothing
 * (LES-057 tolerance: absent -> no breach, never a crash).
 */
export function stationSlaBreachCounts(
  rows: readonly CocStationRowView[],
): Readonly<Record<SlaBreachClass, number>> {
  return slaBreachCountsForRows(
    rows.map((r) => (r.slaChips ?? []).map((c) => c.breachClass)),
  );
}

/** The station's segment filters, in render order, with plain labels. */
export const STATION_SEGMENTS: readonly {
  key: StationSegmentView | 'all';
  label: string;
}[] = [
  { key: 'mine', label: 'My work' },
  { key: 'pool', label: 'Unclaimed' },
  { key: 'all', label: 'All in scope' },
  { key: 'converted_link_pending', label: 'Converted - link pending' },
];

/** Rows for one segment filter ('all' = every working segment EXCEPT the
 *  converted-link-pending shelf, which is identity-repair work, not contact
 *  work -- it renders only under its own filter, never hidden). */
export function rowsForSegment(
  rows: readonly CocStationRowView[],
  segment: StationSegmentView | 'all',
): readonly CocStationRowView[] {
  if (segment === 'all') {
    return rows.filter((r) => r.segment !== 'converted_link_pending');
  }
  return rows.filter((r) => r.segment === segment);
}

/** TRUE when the row's pending callback is DUE (instant <= now). */
export function callbackIsDue(
  row: CocStationRowView,
  nowMs: number,
): boolean {
  if (row.callbackDueAtIso === null) return false;
  const t = Date.parse(row.callbackDueAtIso);
  return Number.isFinite(t) && t <= nowMs;
}

/**
 * The MY WORK header tiles, in order, with scope-honest labels (each label
 * names WHOSE number it is -- the R-47 scope-labeling convention).
 *
 * FIVE TILES BECAME TWO (founder amendment 2026-09-01, "the element count must
 * go down"). The dominant question this screen answers is "what needs my
 * attention?", and each tile below was kept or cut against exactly that:
 *
 *   KEPT  Past SLA (mine)      -- attention-work; no other element on the
 *                                 screen carries this figure at scope level.
 *   KEPT  Callbacks due (mine) -- attention-work with a clock; nothing else
 *                                 on the screen states it at all.
 *   CUT   Assigned to me       -- the "My work" filter chip below already
 *   CUT   New unclaimed        -- states each of these counts, AND the chip is
 *                                 the control that filters to the rows behind
 *                                 it, so the tile was the same number without
 *                                 the door. Worse than redundant: the chips
 *                                 count the CLINIC-FILTERED rows and the tiles
 *                                 counted the whole scope, so with a clinic
 *                                 selected the screen printed two different
 *                                 numbers for one thing. Deleting the tile
 *                                 removes the disagreement as well as the
 *                                 element.
 *   CUT   Booked today         -- not attention-work: nothing on this screen
 *                                 acts on a booking already made. It was also
 *                                 the one figure no loaded row could explain,
 *                                 since the station loads leads and a booking
 *                                 is not a station row. The figure stays in
 *                                 the daily report, which is the screen where
 *                                 booking volume is the question being asked.
 *
 * No capability moved: a cut tile was never clickable (the counts are
 * DISPLAY-ONLY by CROSS-ROLE-HOME-GRAMMAR-V1 D7) and every cut figure is still
 * on the screen or on the screen that owns it.
 */
export function stationTiles(counts: CocStationCountsView): readonly {
  key: string;
  label: string;
  value: number;
  tone: 'neutral' | 'warn' | 'danger';
}[] {
  return [
    {
      key: 'breached',
      label: 'Past SLA (mine)',
      value: counts.slaBreached,
      tone: counts.slaBreached > 0 ? 'danger' : 'neutral',
    },
    {
      key: 'callbacks',
      label: 'Callbacks due (mine)',
      value: counts.callbacksDue,
      tone: counts.callbacksDue > 0 ? 'warn' : 'neutral',
    },
  ];
}

/**
 * THE OPENING LINE FOR THE COC STATION -- the composed clauses only.
 *
 * THE PRIMITIVE IS SHARED AND ALREADY REGISTERED: S1 OPENING LINE,
 * `frontend/src/dashboard/ui/OpeningLine.tsx` (docs/EXPERIENCE-LANGUAGE.md,
 * THE FIVE SIGNATURES; anatomy pinned by tests/unit/design-signatures.test.ts).
 * A SECOND ONE IS FORBIDDEN -- founder instruction 2026-09-01, the Command
 * Center lane adopted it unchanged. This file composes; it renders nothing.
 * The primitive is PRESENTATION-ONLY by its own contract, so the page owns
 * composition and the deterministic rules live here where they can be tested
 * without a DOM.
 *
 * THE CONTRACT THIS COMPOSER OBEYS (S1, owner C-1/C-3):
 *   - 1 to 3 clauses, plain language, above the hero figures;
 *   - DETERMINISTIC: pure rules over figures the page has ALREADY been
 *     served. No new query, no read model change, no historical data, and
 *     never generative;
 *   - EVERY CLAUSE MAPS 1:1 TO A FIGURE VISIBLE ON THIS PAGE -- the two
 *     tiles and the four filter chips. A clause about something the reader
 *     cannot see on the same screen is not lawful here;
 *   - the state vocabulary is the fixed three (healthy / attention / quiet);
 *     ahead-and-behind ride the clause TEXT, never a fourth state.
 *
 * THE SCREEN'S ONE QUESTION IS "WHAT NEEDS MY ATTENTION?", so the STATE is
 * driven by MY work only -- breaches and callbacks. Unclaimed pool leads are
 * real work but they are not yet anyone's, so they ride a clause and never
 * the colour.
 *
 * TWO HAZARDS THIS COMPOSER REFUSES TO PAPER OVER:
 *   1. SCOPE. The tiles count the caller's whole scope, so the clauses read
 *      the UNFILTERED rows. Composing from the clinic-filtered array would
 *      print a line that disagrees with the figures beneath it.
 *   2. CAPPING. When the row slice is capped, "oldest" among LOADED rows may
 *      not be the real oldest, and an understatement reads as reassurance. So
 *      every age qualifier is dropped while capped and the counts stand alone;
 *      the page's existing capped caveat is the honest statement there.
 */
export interface StationOpeningLine {
  readonly state: OpeningLineState;
  readonly clauses: readonly string[];
}

/** The largest SLA-chip age on a row, or null when the row carries none. */
function rowBreachAgeMinutes(row: CocStationRowView): number | null {
  const ages = (row.slaChips ?? []).map((c) => c.ageMinutes).filter((n) =>
    Number.isFinite(n),
  );
  return ages.length === 0 ? null : Math.max(...ages);
}

const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

/**
 * AGE UNIT -- A DELIBERATE DEPARTURE FROM THE FOLLOW-UPS AUTHORITY, STATED.
 * Follow-ups formats ages in whole days ("14 days"). This screen's own SLA
 * chips already render ages through THE ONE leakage humanizer (45m / 2h 15m /
 * 3d), and a station breach is often hours old, which whole-days would flatten
 * to "today". Printing a second age format on one screen would show TWO
 * DIFFERENT AGES FOR THE SAME ROW -- a worse defect than the inconsistency it
 * would fix. So the SHAPE is propagated and the VALUE keeps this screen's
 * shipped humanizer.
 */
export function stationOpeningLine(
  counts: CocStationCountsView,
  allRows: readonly CocStationRowView[],
  nowMs: number,
  capped: boolean,
): StationOpeningLine {
  const mine = allRows.filter((r) => r.segment === 'mine');
  const clauses: string[] = [];

  if (counts.slaBreached > 0) {
    const ages = capped
      ? []
      : mine.map(rowBreachAgeMinutes).filter((n): n is number => n !== null);
    const oldest = ages.length === 0 ? null : slaChipAge(Math.max(...ages));
    clauses.push(
      oldest === null
        ? `${plural(counts.slaBreached, 'lead is', 'leads are')} past SLA.`
        : `${plural(counts.slaBreached, 'lead is', 'leads are')} past SLA, the oldest ${oldest}.`,
    );
  }

  if (counts.callbacksDue > 0) {
    const overdue = capped
      ? []
      : allRows
          .filter((r) => callbackIsDue(r, nowMs))
          .map((r) => (nowMs - Date.parse(r.callbackDueAtIso as string)) / 60000)
          .filter((n) => Number.isFinite(n) && n >= 0);
    const oldest = overdue.length === 0 ? null : slaChipAge(Math.max(...overdue));
    clauses.push(
      oldest === null
        ? `${plural(counts.callbacksDue, 'callback is', 'callbacks are')} due.`
        : `${plural(counts.callbacksDue, 'callback is', 'callbacks are')} due, the oldest ${oldest} ago.`,
    );
  }

  const state: OpeningLineState =
    clauses.length > 0 ? 'attention' : counts.assignedToMe > 0 ? 'healthy' : 'quiet';

  // A ZERO GETS AN HONEST SENTENCE SAYING WHY NOTHING IS THERE, never a
  // comparison and never a trend -- a trend under a zero is meaningless.
  if (state === 'healthy') {
    clauses.push('Nothing is past SLA and no callback is due.');
  } else if (state === 'quiet') {
    clauses.push('No work is assigned to you.');
  }

  // Pool work is visible on the Unclaimed chip, so it is lawful to name -- and
  // it is the one clause that says what to do next when your own list is calm.
  if (counts.newUnclaimed > 0) {
    clauses.push(
      `${plural(counts.newUnclaimed, 'lead is', 'leads are')} unclaimed in your scope.`,
    );
  }

  return { state, clauses };
}

export type StationPrimaryAction =
  | { kind: 'claim'; label: 'Claim' }
  | { kind: 'record_outcome'; label: 'Record outcome' }
  | { kind: 'open_record'; label: 'Open' };

/**
 * ONE primary action per row state (E-3 gate 4):
 *   pool                     -> Claim (the proven writer);
 *   mine                     -> Record outcome (the daily loop);
 *   someone else's / shelf   -> Open (the quick view / record door).
 */
export function primaryActionFor(row: CocStationRowView): StationPrimaryAction {
  if (row.segment === 'pool') return { kind: 'claim', label: 'Claim' };
  if (row.segment === 'mine') {
    return { kind: 'record_outcome', label: 'Record outcome' };
  }
  return { kind: 'open_record', label: 'Open' };
}
